import React, { useContext, useEffect, useState } from "react";
import { LocalizationContext } from "../../../../../../../../locales/Translation";
import { useDispatch, useSelector } from "react-redux";
import {
  dashboardFilterDateRangeSelector,
  dashboardFilterDateTypeSelector,
  dashboardDeskimoDayOfficeBookingFilterSelector,
  dashboardDayOfficeBookingSelector,
  dashboardStatisticFilterSelector,
  filterListPropertySelectedSelector,
} from "../../../../../../selectors/DashboardSelectors";
import {
  dashboardDayOfficeBookingPropertyThunk,
  transactionInfoThunk,
} from "../../../../../../thunks/DashboardThunks";
import SizeBox from "../../../../../../../../components/SizeBox";
import {
  DayOfficeBookingDeskimoType,
  ExtraServiceType,
  TotalExtraServicesFeeType,
} from "../../../../../../configs/DeclareTypes";
import { dashboardDeskimoDayOfficeBookingFilterAction } from "../../../../../../actions/DashboardActions";
import {
  ORDER_BY_ASC,
  ORDER_BY_DESC,
  ORDER_BY_NONE,
} from "../../../../../../../App/configs/Constants";
import TableCustom from "../../../../../../../../components/Table";
import { Link } from "react-router-dom";
import {
  DESKIMO_PROPERTY_DETAIL_LINK,
  DESKIMO_PROPERTY_DETAIL_OFFICES_LINK,
} from "../../../../../../../App/configs/ListRoutes";
import { Popup } from "semantic-ui-react";
import {
  AUTO_REFRESH_INTERVAL_TIME,
  DAY_OFFICE_BOOKING,
  EXTRA_SERVICE_BOOKING_TYPE_DAY_OFFICE,
  FILTER_DATE_TODAY,
} from "../../../../../../configs/Constants";
import BookingActions from "../BookingActions";
import UserNameAndIndicator from "../UserNameAndIndicator";
import ReadMore from "../../../../../../../../components/ReadMore";
import ExtraServicesModal from "../ExtraServicesModal";

const TableDayOfficeBookings = () => {
  const { translations } = useContext(LocalizationContext);
  const dispatch = useDispatch();
  const [loading, setLoading] = useState<boolean>(true);

  const dashboardDayOfficeBooking = useSelector(
    dashboardDayOfficeBookingSelector
  );
  const dashboardDayOfficeBookingFilter = useSelector(
    dashboardDeskimoDayOfficeBookingFilterSelector
  );
  const dashboardFilterDateRange = useSelector(
    dashboardFilterDateRangeSelector
  );
  const filterListPropertySelected = useSelector(
    filterListPropertySelectedSelector
  );
  const dashboardFilterMarket = useSelector(dashboardStatisticFilterSelector);
  const dashboardFilterDateType = useSelector(dashboardFilterDateTypeSelector);

  useEffect(() => {
    if (dashboardFilterDateRange === null) {
      return;
    }

    (async () => await _init())();
    const interval = setInterval(_autoRefresh, AUTO_REFRESH_INTERVAL_TIME);
    return () => {
      clearInterval(interval);
    };
  }, [
    dashboardDayOfficeBookingFilter,
    dashboardFilterDateRange,
    dashboardFilterMarket,
    filterListPropertySelected,
  ]);

  const _init = async () => {
    try {
      setLoading(true);
      const data = await dispatch<any>(
        dashboardDayOfficeBookingPropertyThunk(false, true)
      );

      await _getTransactionInfo(data?.items);
    } catch (e) {
      console.error("Error during initialization:", e);
    } finally {
      setLoading(false);
    }
  };

  const _autoRefresh = async () => {
    if (dashboardFilterDateType !== FILTER_DATE_TODAY) {
      return;
    }
    const data = await dispatch<any>(
      dashboardDayOfficeBookingPropertyThunk(false, true)
    );
    await _getTransactionInfo(data?.items);
  };

  const _getTransactionInfo = async (data: []) => {
    if (!data?.length) {
      return;
    }

    const payload = {
      transaction_ids: data.map(({ id }: any) => id),
      type: DAY_OFFICE_BOOKING,
    };

    await dispatch<any>(transactionInfoThunk(payload));
  };

  const dayOfficeField = ({
    day_office_name,
    day_office_id,
    property_name,
    day_office_image,
    property_id,
    property_company_name,
    property_company_id,
    booking_note,
  }: Partial<DayOfficeBookingDeskimoType>) => (
    <div className={"room-table_meeting-wrapper"}>
      <img src={day_office_image} alt={day_office_name} />
      <div>
        <div className={"table_title"}>{translations.dayOffices.office}:</div>
        <Link
          to={
            DESKIMO_PROPERTY_DETAIL_OFFICES_LINK.replaceAll(
              ":id",
              String(property_id)
            ).replaceAll(":companyId", String(property_company_id)) +
            `?dayOfficeId=${day_office_id}`
          }
        >
          {day_office_name}
        </Link>
        <SizeBox height={"10px"} />
        <div className={"table_title"}>{translations.property}:</div>
        <Popup
          content={property_company_name}
          trigger={
            <Link
              to={DESKIMO_PROPERTY_DETAIL_LINK.replaceAll(
                ":id",
                String(property_id)
              ).replaceAll(":companyId", String(property_company_id))}
            >
              <Popup
                content={property_company_name}
                trigger={<span>{property_name}</span>}
              />
            </Link>
          }
        />

        {booking_note ? (
          <>
            <SizeBox height={"10px"} />
            <div className={"table_title"}>{translations.note}:</div>
            <ReadMore>{booking_note}</ReadMore>
          </>
        ) : null}
      </div>
    </div>
  );

  const dateTimeField = ({
    created_time,
    start_time,
    duration,
  }: Partial<DayOfficeBookingDeskimoType>) => (
    <>
      <div className={"table_title"}>Created time:</div>
      <div>{created_time}</div>
      <SizeBox height={"10px"} />
      <div className={"table_title"}>{translations.startTime}:</div>
      <div>{start_time}</div>
      <SizeBox height={"10px"} />
      <div className={"table_title"}>{translations.duration}:</div>
      <div>
        {duration?.includes("23") && duration?.includes("59")
          ? "Full day"
          : duration}
      </div>
    </>
  );

  const costField = ({
    id,
    total_price,
    discount,
    total_final_price,
    stripe_link,
    extra_services,
    total_extra_services_fee,
  }: {
    id: number;
    hourly_rate: string;
    total_price: string;
    discount?: string;
    total_final_price?: string;
    stripe_link?: string;
    extra_services?: ExtraServiceType[];
    total_extra_services_fee?: TotalExtraServicesFeeType;
  }) => (
    <>
      <div className={"table_title"}>Price:</div>
      <div>
        {stripe_link ? (
          <a
            href={stripe_link}
            target={"_blank"}
            className={"text-nowrap"}
            rel="noreferrer"
          >
            {total_price} <i className="uil uil-link-alt"></i>
          </a>
        ) : (
          total_price
        )}
      </div>
      {discount ? (
        <>
          <SizeBox height={"10px"} />
          <div className={"table_title"}>Discount:</div>
          <div>{discount}</div>
        </>
      ) : null}

      {total_final_price ? (
        <>
          <SizeBox height={"10px"} />
          <div className={"table_title"}>Final Price:</div>
          <div>
            {stripe_link ? (
              <a
                href={stripe_link}
                target={"_blank"}
                className={"text-nowrap"}
                rel="noreferrer"
              >
                {total_final_price} <i className="uil uil-link-alt"></i>
              </a>
            ) : (
              total_final_price
            )}
          </div>
        </>
      ) : null}

      <ExtraServicesModal
        bookingId={id}
        bookingType={EXTRA_SERVICE_BOOKING_TYPE_DAY_OFFICE}
        extraServices={extra_services}
        totalExtraServicesFee={total_extra_services_fee}
        onCancelled={_init}
      />
    </>
  );

  const statusField = ({
    status_name,
    cancellation_request,
  }: Partial<DayOfficeBookingDeskimoType>) => (
    <>
      <div>
        <b>Status: </b>
        <div>{status_name}</div>
      </div>
      <SizeBox height={"10px"} />
      <div>
        <b>Cancellation request: </b>
        {cancellation_request}
      </div>
    </>
  );

  const _formatInputData = () => {
    return dashboardDayOfficeBooking.items.map(
      ({
        transaction_id,
        employee_name,
        property_name,
        start_time,
        duration,
        hourly_rate,
        total_price,
        refund_amount,
        refund_status_id,
        day_office_name,
        day_office_image,
        day_office_id,
        status_id,
        actions,
        id,
        employee_id,
        from_source,
        company,
        is_company_payment_method,
        phone,
        employee_avatar,
        cap_name,
        property_id,
        created_time,
        status_name,
        is_request_cancellation,
        refund_type,
        price_after_discount,
        price_after_discount_amount,
        free_credit_amount,
        currency,
        discount,
        total_final_price,
        property_company_name,
        property_company_id,
        can_pay_with_credit,
        cancellation_request,
        stripe_link,
        booking_note,
        extra_services,
        total_extra_services_fee,
      }: DayOfficeBookingDeskimoType) => {
        return {
          transaction_id,
          userName: (
            <UserNameAndIndicator
              transactionId={id}
              userName={employee_name}
              userId={employee_id}
              showAvatar
              avatarUrl={employee_avatar}
              capName={cap_name}
              isCompanyPaymentMethod={is_company_payment_method}
              companyName={company}
            />
          ),
          phone: phone || "-",
          from_source,
          dayOfficeField: dayOfficeField({
            property_name,
            day_office_name,
            day_office_image,
            property_id,
            property_company_name,
            day_office_id,
            property_company_id,
            booking_note,
          }),
          dateTimeField: dateTimeField({ created_time, start_time, duration }),
          costField: costField({
            id,
            hourly_rate,
            total_price,
            discount,
            total_final_price,
            extra_services,
            total_extra_services_fee,
            stripe_link,
          }),
          statusField: statusField({
            status_id,
            refund_amount,
            refund_status_id,
            status_name,
            is_request_cancellation,
            cancellation_request,
          }),
          action: (
            <BookingActions
              statusId={status_id}
              bookingId={id}
              actions={actions}
              onCallback={_init}
              setRefresh={_init}
              refundType={refund_type}
              priceAfterDiscount={price_after_discount}
              priceAfterDiscountAmount={price_after_discount_amount}
              freeCreditAmount={free_credit_amount}
              currency={currency}
              type={DAY_OFFICE_BOOKING}
              canPayWithCredit={can_pay_with_credit}
            />
          ),
        };
      }
    );
  };

  const columns = [
    { key: "day_office_by_transaction_id", col: translations.transactionID },
    { key: "day_office_by_employee_name", col: translations.userName },
    { key: "", col: translations.phoneNo },
    { key: "day_office_by_employee_source", col: "Source" },
    { key: "day_office_by_day_office", col: translations.dayOffices.office },
    { key: "day_office_by_start_time", col: "Date" },
    { key: "day_office_by_cost", col: translations.revenue },
    { key: "day_office_by_status", col: translations.status },
    { key: "day_office_by_action", col: translations.action },
  ];

  const _onChangeSortCol = (col: any) => {
    if (dashboardDayOfficeBookingFilter.sort_by !== col.key) {
      dispatch(
        dashboardDeskimoDayOfficeBookingFilterAction({
          key: "sort_by",
          value: col.key,
        })
      );
      dispatch(
        dashboardDeskimoDayOfficeBookingFilterAction({
          key: "order_by",
          value: ORDER_BY_ASC,
        })
      );
    } else {
      dispatch(
        dashboardDeskimoDayOfficeBookingFilterAction({
          key: "sort_by",
          value: col.key,
        })
      );
      if (dashboardDayOfficeBookingFilter.order_by === ORDER_BY_NONE) {
        dispatch(
          dashboardDeskimoDayOfficeBookingFilterAction({
            key: "order_by",
            value: ORDER_BY_ASC,
          })
        );
      }
      if (dashboardDayOfficeBookingFilter.order_by === ORDER_BY_ASC) {
        dispatch(
          dashboardDeskimoDayOfficeBookingFilterAction({
            key: "order_by",
            value: ORDER_BY_DESC,
          })
        );
      }
      if (dashboardDayOfficeBookingFilter.order_by === ORDER_BY_DESC) {
        dispatch(
          dashboardDeskimoDayOfficeBookingFilterAction({
            key: "order_by",
            value: ORDER_BY_NONE,
          })
        );
      }
    }
  };

  return (
    <TableCustom
      orderBy={dashboardDayOfficeBookingFilter?.order_by}
      sortBy={dashboardDayOfficeBookingFilter?.sort_by}
      sortTable={true}
      onChangeSortCol={_onChangeSortCol}
      tableData={
        dashboardDayOfficeBooking?.items?.length > 0 ? _formatInputData() : []
      }
      columns={columns}
      loading={loading}
      totalItems={dashboardDayOfficeBooking?.pagination?.total}
      currentPage={dashboardDayOfficeBooking?.pagination?.page}
      showTotal
      unit={translations.transactions}
      itemPerPage={dashboardDayOfficeBooking?.pagination?.limit}
      onChangePagination={(_: any, { activePage }: any) => {
        dispatch(
          dashboardDeskimoDayOfficeBookingFilterAction({
            key: "page",
            value: activePage,
          })
        );
      }}
    />
  );
};

export default TableDayOfficeBookings;
